// src/modules/time-tracking/ShiftFormModal.js
//
// Admin create/edit of a shift (register). Used to backfill a forgotten
// clock-in/out or correct hours. Self-contained overlay (wider than the shared
// confirm Modal so the datetime pickers have room).
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import Select from '../../components/shared/Select';
import Input from '../../components/shared/Input';
import { createShift, updateShift, listSites } from './timeTrackingService';

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 1200;
  display: flex; justify-content: center; align-items: flex-start;
  overflow-y: auto; padding: 40px 16px 80px;
`;
const Content = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  box-shadow: 0 8px 24px rgba(0,0,0,0.4); width: 540px; max-width: 100%; margin: auto;
  padding: ${(p) => p.theme.spacing(3)};
`;
const Head = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(3)};
  h2 { margin: 0; font-size: 1.25rem; color: ${(p) => p.theme.colors.textPrimary}; }
  button { background: none; border: none; color: ${(p) => p.theme.colors.textSecondary}; cursor: pointer; font-size: 1.2rem; }
`;
const Field = styled.div`
  margin-bottom: ${(p) => p.theme.spacing(2)};
  label { display: block; color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.85rem; margin-bottom: 6px; }
  .react-datepicker-wrapper { width: 100%; }
  input.dt { width: 100%; background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
    border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
    padding: 12px 14px; font-size: 1rem; }
`;
const ErrorText = styled.div`color: ${(p) => p.theme.colors.error}; font-size: 0.85rem; margin: 4px 0 12px;`;
const CheckRow = styled.label`
  display: flex; align-items: center; gap: 10px; cursor: pointer; user-select: none;
  margin: ${(p) => p.theme.spacing(2)} 0; color: ${(p) => p.theme.colors.textPrimary}; font-size: 0.9rem;
  input { width: 18px; height: 18px; accent-color: ${(p) => p.theme.colors.primary}; cursor: pointer; }
  small { display: block; color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.78rem; }
`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 10px; margin-top: ${(p) => p.theme.spacing(3)};`;
const Btn = styled.button`
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer; font-weight: 500;
  padding: 12px 20px; display: flex; align-items: center; gap: 8px;
  background: ${(p) => (p.$variant === 'cancel' ? 'transparent' : p.theme.colors.primary)};
  color: ${(p) => (p.$variant === 'cancel' ? p.theme.colors.textSecondary : p.theme.colors.black)};
  border: ${(p) => (p.$variant === 'cancel' ? `1px solid ${p.theme.colors.inputGray}` : 'none')};
  &:disabled { opacity: 0.6; cursor: default; }
`;

const ShiftFormModal = ({ isOpen, mode, shift, employees = [], onClose, onSaved }) => {
  const isEdit = mode === 'edit';
  const [userId, setUserId] = useState('');
  const [clockIn, setClockIn] = useState(null);
  const [clockOut, setClockOut] = useState(null);
  const [siteId, setSiteId] = useState('');
  const [wageRate, setWageRate] = useState('');
  const [notes, setNotes] = useState('');
  const [reviewFlag, setReviewFlag] = useState(false);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    listSites().then((d) => setSites(d.sites || [])).catch(() => setSites([]));
    if (isEdit && shift) {
      setUserId(String(shift.user_id || ''));
      setClockIn(shift.clock_in_at ? new Date(shift.clock_in_at) : null);
      setClockOut(shift.clock_out_at ? new Date(shift.clock_out_at) : null);
      setWageRate(shift.wage_rate != null ? String(shift.wage_rate) : '');
      setNotes(shift.notes || '');
      setReviewFlag(!!shift.review_flag);
      setSiteId('');
    } else {
      setUserId(''); setClockIn(null); setClockOut(null);
      setSiteId(''); setWageRate(''); setNotes(''); setReviewFlag(false);
    }
  }, [isOpen, isEdit, shift]);

  if (!isOpen) return null;

  const validate = () => {
    if (!isEdit && !userId) return 'Select an employee';
    if (!clockIn) return 'Clock-in time is required';
    if (!clockOut) return 'Clock-out time is required';
    if (clockOut <= clockIn) return 'Clock-out must be after clock-in';
    if (wageRate !== '' && (isNaN(parseFloat(wageRate)) || parseFloat(wageRate) < 0)) return 'Invalid pay rate';
    return '';
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true); setError('');
    try {
      const wage = wageRate === '' ? null : parseFloat(wageRate);
      if (isEdit) {
        await updateShift(shift.id, {
          clock_in_at: clockIn.toISOString(),
          clock_out_at: clockOut.toISOString(),
          wage_rate: wage,
          notes,
          review_flag: reviewFlag,
        });
      } else {
        await createShift({
          userId: parseInt(userId, 10),
          clock_in_at: clockIn.toISOString(),
          clock_out_at: clockOut.toISOString(),
          siteId: siteId || undefined,
          wage_rate: wage,
          notes,
        });
      }
      onSaved?.(isEdit ? 'Shift updated' : 'Shift created');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Content>
        <Head>
          <h2>{isEdit ? 'Edit Shift' : 'Add Shift'}</h2>
          <button type="button" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </Head>

        {!isEdit && (
          <Field>
            <Select label="Employee" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select employee…</option>
              {employees.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
            </Select>
          </Field>
        )}
        {isEdit && (
          <Field>
            <label>Employee</label>
            <div style={{ color: '#fff' }}>{shift?.User?.username || `#${shift?.user_id}`}</div>
          </Field>
        )}

        <Field>
          <label>Clock In</label>
          <DatePicker selected={clockIn} onChange={setClockIn} showTimeSelect
            dateFormat="MMM d, yyyy h:mm aa" maxDate={new Date()}
            placeholderText="Select clock-in time" className="dt" />
        </Field>
        <Field>
          <label>Clock Out</label>
          <DatePicker selected={clockOut} onChange={setClockOut} showTimeSelect
            dateFormat="MMM d, yyyy h:mm aa" maxDate={new Date()}
            placeholderText="Select clock-out time" className="dt" />
        </Field>

        {!isEdit && (
          <Field>
            <Select label="Work site (optional)" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">No specific site</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
        )}

        <Input label="Pay rate override ($/hr, optional)" type="number" min="0" step="0.01"
          value={wageRate} onChange={(e) => setWageRate(e.target.value)}
          placeholder="Defaults to the employee's rate" />

        <Input label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Backfilled — forgot to clock out" />

        {isEdit && (
          <CheckRow>
            <input type="checkbox" checked={reviewFlag} onChange={(e) => setReviewFlag(e.target.checked)} />
            <span>Flagged for review
              <small>Uncheck to mark this shift as reviewed / resolved.</small>
            </span>
          </CheckRow>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <Actions>
          <Btn type="button" $variant="cancel" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn type="button" onClick={handleSave} disabled={saving}>
            {saving ? <><FaSpinner className="icon-spin" /> Saving…</> : <><FaSave /> {isEdit ? 'Update' : 'Create'}</>}
          </Btn>
        </Actions>
      </Content>
    </Overlay>
  );
};

export default ShiftFormModal;
